<template>
  <div class="aiw2-draft">
    <hero title="Draft" :keyword="keyword" :mode="mode" :page-run="pageRun"
      info-title="First POP plans the sections of your article, then it writes them. You can edit, lock, or regenerate any section before moving on." />

    <!-- Fast Draft: full-page generation progress (POP writes everything in one
         pass). On completion the checklist stays visible in its finished state
         (all stages ✓) with a CTA to review — it does not vanish. -->
    <div v-if="fastGenerating || fastDone" class="draft-progress" :class="{ 'is-done': fastDone }">
      <div class="draft-progress-head">
        <h4>{{ fastDone ? 'Your article is ready' : 'Writing your article' }}</h4>
        <span class="word-pill">{{ fastDone ? 100 : fastPct }}%</span>
      </div>
      <div class="progress-bar-wrap"><div class="progress-bar" :style="{ width: (fastDone ? 100 : fastPct) + '%' }"></div></div>
      <div class="progress-list">
        <div v-for="(s, i) in FAST_STAGES" :key="s.key" class="progress-row" :class="fastStageStatus(i)">
          <div class="check"><span v-if="fastStageStatus(i) === 'done'">✓</span><span v-else>{{ i + 1 }}</span></div>
          <div class="label">{{ s.label }}</div>
          <span v-if="fastStageStatus(i) === 'active'" class="spinner"></span>
        </div>
      </div>
      <div class="draft-progress-foot">
        <template v-if="fastDone">
          <div class="current-preview"><span>All sections written. <strong>Your article is ready to review.</strong></span></div>
          <button class="aiw2-btn aiw2-btn--primary" @click="$emit('fast-continue')">Review your article →</button>
        </template>
        <div v-else class="current-preview"><span>Currently: <strong>{{ fastLabel }}</strong></span></div>
      </div>
    </div>

    <div v-if="!fastGenerating" class="draft-tabs-wrap">
      <div class="draft-tabs">
        <button class="draft-tab" :class="{ active: tab === 'structure', done: hasHeadings }" @click="tab = 'structure'">
          <span class="draft-tab-num"><span>1</span></span> Structure
        </button>
        <button class="draft-tab" :class="{ active: tab === 'content', done: blocksGenerated }" :disabled="!blocksGenerated && !drafting" @click="tab = 'content'">
          <span class="draft-tab-num"><span>2</span></span> Content
        </button>
      </div>

      <!-- TAB 1: Structure -->
      <div v-show="tab === 'structure'" class="draft-tab-panel">
        <!-- Sub-headings config — stays visible after generation (design parity:
             #structureSetup is never hidden). The CTA becomes "Regenerate
             sub-headings" and the review is revealed below it. -->
        <div class="title-config">
          <div class="title-config-head">
            How should POP build your sub-headings (H2 / H3)?
            <v-tooltip top color="transparent">
              <template v-slot:activator="{ on, attrs }">
                <span class="info-ico" v-bind="attrs" v-on="on"></span>
              </template>
              <div class="white-bg-tooltip small-tooltip">Sub-headings are the section titles that break your article up: H2 for main sections, H3 for points inside them. POP plans them before writing so the article has a sensible shape.</div>
            </v-tooltip>
          </div>

          <label class="radio-row" :class="{ selected: subApproach === 'generate' }" @click="subApproach = 'generate'">
            <span class="radio-dot"></span>
            <span><strong>Generate new or rewrite my H2 and H3 sub-headings</strong> - POP plans the section structure from your brief and recommendations.</span>
          </label>
          <label class="radio-row" :class="{ selected: subApproach === 'leave', disabled: pageNotBuilt }" @click="!pageNotBuilt && (subApproach = 'leave')">
            <span class="radio-dot"></span>
            <span>I don't want to generate new H2 and H3 sub-headings with AI <strong>(leave them as is)</strong></span>
            <v-tooltip top color="transparent">
              <template v-slot:activator="{ on, attrs }">
                <span class="info-ico" v-bind="attrs" v-on="on" @click.stop></span>
              </template>
              <div class="white-bg-tooltip small-tooltip">{{ pageNotBuilt ? "Your page isn't built yet, so there are no existing sub-headings to keep." : "Keep the section headings you already have. POP will write the content underneath them but won't rename or reorder anything." }}</div>
            </v-tooltip>
          </label>

          <div v-if="subApproach === 'generate'" class="title-config-toggles">
            <div class="tct-label">Extra inspiration</div>
            <div class="toggle-row" @click="useExistingH2s = !useExistingH2s">
              <button type="button" class="toggle" :class="{ on: useExistingH2s }"></button>
              <span>Use my existing H2s for inspiration</span>
              <v-tooltip top color="transparent">
                <template v-slot:activator="{ on, attrs }">
                  <span class="info-ico" v-bind="attrs" v-on="on" @click.stop></span>
                </template>
                <div class="white-bg-tooltip small-tooltip">POP starts from the section headings you already use and keeps a similar structure and voice, rather than inventing a new outline from scratch.</div>
              </v-tooltip>
            </div>
            <div class="toggle-row" @click="useCompetitorH2s = !useCompetitorH2s">
              <button type="button" class="toggle" :class="{ on: useCompetitorH2s }"></button>
              <span>Also use my competitors' H2s for inspiration</span>
              <v-tooltip top color="transparent">
                <template v-slot:activator="{ on, attrs }">
                  <span class="info-ico" v-bind="attrs" v-on="on" @click.stop></span>
                </template>
                <div class="white-bg-tooltip small-tooltip">POP checks what sections the top-ranking pages cover, so your article doesn't miss a topic readers expect. It writes its own headings, not copies of theirs.</div>
              </v-tooltip>
            </div>
          </div>

          <div class="title-gen-bar">
            <button class="aiw2-btn aiw2-btn--primary" :disabled="generatingStructure || busy" @click="onGenerateStructure">
              <span v-if="generatingStructure" class="spin"></span>
              <svg v-else-if="hasHeadings && subApproach !== 'leave'" class="gen-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
              <svg v-else class="gen-ico" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.09 5.91L20 10l-5.91 2.09L12 18l-2.09-5.91L4 10l5.91-2.09z"/></svg>
              {{ genStructureLabel }}
            </button>
          </div>

          <div v-if="generatingStructure" class="title-progress">
            <div class="title-progress-label"><span class="prog-glyph">✦</span> POP is planning your sections…</div>
            <div class="title-progress-track"><div class="title-progress-fill" :style="{ width: genPct + '%' }"></div></div>
          </div>
          <div v-if="footerError" class="err">{{ footerError }}</div>
        </div>

        <!-- Structure review — revealed once an outline exists, below the config -->
        <template v-if="hasHeadings">
          <div class="st-intro">
            Review the sections, word counts, and visual content POP will use before writing.
            <strong>Click any heading to edit it.</strong> Drag to reorder - moving an H2 carries its H3s.
          </div>
          <structure-tab
            :rows="rows"
            :busy="busy"
            @reorder="onReorder"
            @edit-text="onEditText"
            @toggle-kind="onToggleKind"
            @lock="onLockRow"
            @copy-row="onCopyRow"
            @delete-row="onDeleteRow"
            @add-heading="onAddHeading"
            @add-bulk="openBulkHeadings"
            @add-visual="onAddVisual"
            @regenerate-all="openRegenAll"
            @download="downloadOutline"
            @copy-outline="copyOutline"
            @history="openStructureHistory"
          />
          <div class="generate-cta">
            <div class="generate-cta-text">
              <h4>Ready to write?</h4>
              <p>POP will write each section together with its visual content, preserving any locked sections. Estimated <strong>{{ estWords }} words</strong> · <strong>{{ estCredits }} credits</strong>.</p>
            </div>
            <button class="aiw2-btn aiw2-btn--primary" :disabled="drafting || busy" @click="generateDraft">⚡ Generate draft</button>
          </div>
        </template>
      </div>

      <!-- TAB 2: Content -->
      <div v-show="tab === 'content'" class="draft-tab-panel">
        <div v-if="!blocksGenerated && !drafting" class="tab-empty">
          <h4>No draft yet</h4>
          <p>Approve the structure first, then POP will write your article here. Click <strong>Generate draft</strong> in the bottom bar when you're ready.</p>
          <button class="aiw2-btn aiw2-btn--ghost" @click="tab = 'structure'">← Back to Structure</button>
        </div>
        <content-tab
          v-else
          :blocks="blocks"
          :image-busy="imageBusy"
          :drafting="drafting"
          :progress="progress"
          @edit="onBlockEdit"
          @lock="onBlockLock"
          @delete="onBlockDelete"
          @regenerate="openRegenConfirm"
          @again="openAgainModal"
          @toc-options="onTocOptions"
          @history="openHistory"
          @image="onGenerateImage"
          @alt-edit="onAltEdit"
        />
      </div>
    </div>

    <!-- bulk add sub-headings modal -->
    <div v-if="bulkModal.show" class="aiw2-overlay" @click.self="bulkModal.show = false">
      <div class="aiw2-modal">
        <h4>Add sub-headings in bulk</h4>
        <p>Paste your sub-headings using <code>&lt;h2&gt;</code> and <code>&lt;h3&gt;</code> tags only - no paragraphs or other HTML.</p>
        <textarea class="aiw2-textarea" rows="8" v-model="bulkModal.text" :placeholder="BULK_HEADINGS_PLACEHOLDER"></textarea>
        <p class="aiw2-help">Each H3 nests under the sub-heading above it. New sub-headings are added at the end of the outline.</p>
        <div v-if="bulkModal.error" class="err">{{ bulkModal.error }}</div>
        <div class="modal-actions">
          <button class="aiw2-btn" @click="bulkModal.show = false">Cancel</button>
          <button class="aiw2-btn aiw2-btn--primary" :disabled="!bulkModal.text.trim()" @click="confirmBulkHeadings">Add sub-headings</button>
        </div>
      </div>
    </div>

    <!-- Regenerate sub-headings modal (matches the "Regenerate this section" design) -->
    <div v-if="regenAllModal.show" class="regen-overlay" @click.self="regenAllModal.show = false">
      <div class="regen-modal regen-again">
        <div class="ra-head">
          <h3>Regenerate sub-headings</h3>
          <button class="ra-close" aria-label="Close" @click="regenAllModal.show = false">×</button>
        </div>
        <label class="ra-label">What would you like POP to change? <span class="ra-label-note">Locked sections are preserved.</span></label>
        <textarea
          class="ra-input"
          rows="3"
          v-model="regenAllModal.text"
          placeholder="e.g. fewer sections, and add one comparing the options"
        ></textarea>
        <div class="ra-sugg-label">Or pick a quick improvement</div>
        <div class="ra-suggs">
          <button
            v-for="s in SUBHEADING_SUGGESTIONS"
            :key="s.label"
            class="ra-sugg"
            :class="{ active: regenAllModal.picked.indexOf(s.text) !== -1 }"
            @click="toggleRegenAllSugg(s.text)"
          >{{ s.label }}</button>
        </div>
        <div v-if="footerError" class="err">{{ footerError }}</div>
        <div class="ra-foot">
          <div class="ra-cost">
            <span class="ra-cost-ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            </span>
            <span v-if="unlimitedRegen">Regenerates are free on your account</span>
            <span v-else-if="paidRegenFree">Free — this article uses your own API key</span>
            <span v-else>Costs <strong>1 regenerate credit</strong> · {{ freeRemaining }} free left</span>
          </div>
          <div class="ra-actions">
            <button class="btn-cancel" @click="regenAllModal.show = false">Cancel</button>
            <button class="btn-regen" :disabled="busy" @click="confirmRegenAll">{{ paidRegenFree ? 'Regenerate' : 'Regenerate · 1 credit' }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- block regenerate credit modal -->
    <div v-if="regenModal.show" class="aiw2-overlay" @click.self="regenModal.show = false">
      <div class="aiw2-modal">
        <h4>{{ paidRegenFree ? 'Regenerate this section?' : 'Use 1 regenerate credit?' }}</h4>
        <p v-if="paidRegenFree">Regenerating this section is free on this article.</p>
        <p v-else>Regenerating this section uses <strong>1 regenerate credit</strong>. You have <strong>{{ freeRemaining }} free</strong> left.</p>
        <div v-if="regenError" class="err">{{ regenError }}</div>
        <div class="modal-actions">
          <button class="aiw2-btn" @click="regenModal.show = false">Cancel</button>
          <button class="aiw2-btn aiw2-btn--primary" :disabled="busy" @click="confirmRegen">{{ paidRegenFree ? 'Regenerate' : 'Regenerate · 1 credit' }}</button>
        </div>
      </div>
    </div>

    <!-- "Again" instruction modal (design handoff: .regen-modal.regen-again) -->
    <div v-if="againModal.show" class="regen-overlay" @click.self="againModal.show = false">
      <div class="regen-modal regen-again">
        <div class="ra-head">
          <h3>{{ againTitle }}</h3>
          <button class="ra-close" aria-label="Close" @click="againModal.show = false">×</button>
        </div>
        <label class="ra-label">What would you like POP to change?</label>
        <textarea
          ref="raInput"
          class="ra-input"
          rows="3"
          v-model="againModal.text"
          placeholder="e.g. keep the Venice details but make the tone warmer and add a sentence about luggage storage"
        ></textarea>
        <div class="ra-sugg-label">Or pick a quick improvement</div>
        <div class="ra-suggs">
          <button
            v-for="s in REGEN_QUICK_SUGGESTIONS"
            :key="s.label"
            class="ra-sugg"
            :class="{ active: againModal.picked.indexOf(s.text) !== -1 }"
            @click="toggleSugg(s.text)"
          >{{ s.label }}</button>
        </div>
        <div v-if="regenError" class="err">{{ regenError }}</div>
        <div class="ra-foot">
          <div class="ra-cost">
            <span class="ra-cost-ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            </span>
            <!-- One span so the label is a single flex item: otherwise each text run
                 and the <strong> become separate items, each centred on its own box
                 (bold resolves line-height differently, so it sat low) and the 7px
                 gap applied around the bolded credit too. -->
            <span v-if="paidRegenFree">{{ unlimitedRegen ? 'Regenerates are free on your account' : 'Free — this article uses your own API key' }}</span>
            <span v-else>Costs <strong>1 regenerate credit</strong> · {{ freeRemaining }} free left</span>
          </div>
          <div class="ra-actions">
            <button class="btn-cancel" @click="againModal.show = false">Cancel</button>
            <button class="btn-regen" :disabled="busy" @click="confirmAgain">{{ paidRegenFree ? 'Regenerate' : 'Regenerate · 1 credit' }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- section (block) history modal -->
    <history-modal
      :show="historyModal.show"
      title="Section history"
      intro="Every generate, edit and fix is saved. Restore any version to bring back its content."
      :items="historyModal.items"
      @close="historyModal.show = false"
      @restore="restoreVersion"
    />

    <!-- outline (structure) history modal -->
    <history-modal
      :show="structHistoryModal.show"
      title="Outline history"
      intro="Every generate and edit is saved. Restore any version to bring back its full outline."
      :items="structHistoryModal.items"
      @close="structHistoryModal.show = false"
      @restore="restoreStructureVersion"
    />
  </div>
</template>

<script>
import Hero from "../../components/Hero.vue";
import StructureTab from "./StructureTab.vue";
import ContentTab from "./ContentTab.vue";
import HistoryModal from "../../components/HistoryModal.vue";
import { REGEN_QUICK_SUGGESTIONS, SUBHEADING_SUGGESTIONS, VISUAL_PLACEHOLDER_TYPES } from "../../constants";
import { downloadAsHtml } from "@/components/FullPageSourceEditorV2/components/AiWriter/helper";
import { outlineHtmlFromRows, parseHeadingsHtml } from "../../utils/outline";
import {
  aiWriterStructureGenerate, aiWriterStructureRegenerate, aiWriterStructureSave,
  aiWriterStructureLeaveAsIs, aiWriterStructureExport, aiWriterDraftGenerate,
  aiWriterStructureHistory, aiWriterStructureRestore,
  aiWriterGetBlocks, aiWriterBlockPatch, aiWriterBlockDelete, aiWriterBlockRegenerate,
  aiWriterBlockHistory, aiWriterBlockRestore, aiWriterBlockImage, aiWriterContinueToSeo,
  aiWriterGetProgress, aiWriterGetState,
} from "@/api";

// Example markup shown in the bulk sub-headings modal's empty textarea.
const BULK_HEADINGS_PLACEHOLDER = [
  "<h2>Introduction to On-Page SEO</h2>",
  "<h3>What Is On-Page SEO?</h3>",
  "<h2>Benefits of SEO</h2>",
  "<h3>Increased Visibility</h3>",
].join("\n");

// Fast Draft stages, matching the backend ai_writer_fast_draft_task sub_steps.
const FAST_STAGES = [
  { key: "planning", label: "Planning your article" },
  { key: "title", label: "Choosing the best title" },
  { key: "structure", label: "Designing the outline" },
  { key: "draft", label: "Writing your article" },
  { key: "analyze", label: "Scoring & checking quality" },
  { key: "fix", label: "Polishing and fixing" },
];

export default {
  name: "AiwV2DraftStep",
  components: { Hero, StructureTab, ContentTab, HistoryModal },
  props: {
    aiRunId: { default: null },
    keyword: { type: String, default: "" },
    mode: { type: String, default: "" },
    pageRun: { type: Object, default: () => ({}) },
    planning: { type: Object, default: () => ({}) },
    setup: { type: Object, default: () => ({}) },
    initialStructure: { type: Object, default: () => ({}) },
    credits: { type: Object, default: () => ({}) },
    // Server credit estimate — its `draft_credits` is the authoritative draft
    // charge shown in the "Ready to write?" box (same figure as the bottom bar).
    creditEstimate: { type: Object, default: null },
    // Fast Draft: when true, render the one-pass generation progress instead of
    // the structure/content tabs; `fastProgress` is the live fast_draft progress.
    fastGenerating: { type: Boolean, default: false },
    fastProgress: { type: Object, default: null },
    // Fast Draft finished: keep the completed checklist on screen (all stages ✓)
    // with a "Review your article" CTA instead of letting the panel disappear.
    fastDone: { type: Boolean, default: false },
  },
  data() {
    const structure = this.initialStructure || {};
    return {
      REGEN_QUICK_SUGGESTIONS,
      SUBHEADING_SUGGESTIONS,
      FAST_STAGES,
      BULK_HEADINGS_PLACEHOLDER,
      bulkModal: { show: false, text: "", error: "" },
      tab: "structure",
      subApproach: "generate",
      useExistingH2s: false,
      useCompetitorH2s: false,
      rows: (structure.rows || []).slice(),
      blocks: [],
      blocksGenerated: false,
      generatingStructure: false,
      drafting: false,
      busy: false,
      // True while an image is generating — serializes image generation so two
      // image tasks never run at once (concurrent tasks corrupt the shared blob).
      imageBusy: false,
      progress: {},
      footerError: "",
      regenError: "",
      regenAllModal: { show: false, text: "", picked: [] },
      regenModal: { show: false, block: null, mode: null },
      againModal: { show: false, text: "", block: null, picked: [] },
      historyModal: { show: false, items: [], block: null },
      structHistoryModal: { show: false, items: [] },
      _poll: null,
      _saveTimer: null,
      genPct: 0,
      _genTrickle: null,
    };
  },
  computed: {
    token() {
      return this.$store.getters.getToken;
    },
    unlimitedRegen() {
      return !!(this.credits && this.credits.unlimitedRegenerate);
    },
    freeRemaining() {
      return this.credits && this.credits.regenerateRemaining != null ? this.credits.regenerateRemaining : 5;
    },
    // Own-key runs consume the 5 free regenerates first, then stay free — so
    // once the pool is empty, treat regenerates like the unlimited case.
    ownKeyFree() {
      return !!(this.credits && this.credits.ownKeyFree);
    },
    paidRegenFree() {
      return this.unlimitedRegen || (this.ownKeyFree && this.freeRemaining === 0);
    },
    // Rich regenerate modal heading, context-aware for the visual types that use
    // it (same design, adapted label — like "Regenerate titles"/"sub-headings").
    againTitle() {
      const t = this.againModal.block && this.againModal.block.type;
      if (t === "table") return "Regenerate this table";
      if (t === "list") return "Regenerate this list";
      return "Regenerate this section";
    },
    // "Leave them as is" needs existing page sub-headings to keep — impossible
    // on a page-not-built report, so the option is disabled there.
    pageNotBuilt() {
      return !!(this.pageRun && this.pageRun.pageNotBuilt);
    },
    // Sub-headings exist only when the outline actually has H2/H3 rows. A structure
    // can be non-empty yet heading-less (visual/sources rows backfilled around an
    // empty plan), which used to hide the Stage-A config with no way back.
    hasHeadings() {
      return this.rows.some((r) => r.kind === "h2" || r.kind === "h3");
    },
    genStructureLabel() {
      if (this.subApproach === "leave") return "Continue";
      return this.hasHeadings ? "Regenerate sub-headings" : "Generate sub-headings";
    },
    _estWordCount() {
      const fromRows = this.rows.reduce((s, r) => s + (r.target_words || 0), 0);
      return fromRows || (this.setup && this.setup.word_count) || 0;
    },
    estWords() {
      return this._estWordCount.toLocaleString();
    },
    estCredits() {
      // Prefer the server-authoritative draft charge (article + non-image rich;
      // images billed per-image) so this matches the bottom bar exactly. The
      // rough local estimate is only a fallback until the estimate loads.
      if (this.creditEstimate && this.creditEstimate.draft_credits != null) {
        return this.creditEstimate.draft_credits;
      }
      return Math.max(1, Math.round((this._estWordCount / 1000) * 3));
    },
    // Fast Draft is truly finished only on the orchestrator's own terminal write
    // (phase 'fast_draft'). Its in-process sub-tasks leak status:'complete' with
    // their own phase at each stage boundary — those must NOT read as "all done",
    // or the active-stage spinner/pulse disappears mid-run.
    fastComplete() {
      const p = this.fastProgress || {};
      return p.phase === "fast_draft" && p.status === "complete";
    },
    fastStageIndex() {
      const p = this.fastProgress || {};
      // Between stages the orchestrator reports phase 'fast_draft' + a sub_step;
      // while a stage runs, its sub-task reports its own phase (planning/title/
      // structure/draft/analyze/fix). Match either to the stage list.
      const key = (p.phase && p.phase !== "fast_draft") ? p.phase : p.sub_step;
      const idx = FAST_STAGES.findIndex((s) => s.key === key);
      return idx < 0 ? 0 : idx;
    },
    fastPct() {
      // Steady stage-based bar — sub-tasks report their own 0-100 pct, so a raw
      // bar would jump backwards on each stage. Derive from the stage instead.
      if (this.fastComplete) return 100;
      return Math.round(((this.fastStageIndex + 0.5) / FAST_STAGES.length) * 100);
    },
    fastLabel() {
      const p = this.fastProgress || {};
      const stage = FAST_STAGES[this.fastStageIndex];
      return p.label || (stage && stage.label) || "Preparing your brief…";
    },
    // Shared bottom-bar config — three sub-steps (design parity): generate the
    // outline, then write the draft, then continue. Nothing auto-runs.
    barState() {
      // Fast Draft runs unattended — no bottom-bar action while it generates, and
      // once done the in-panel "Review your article" CTA owns the hand-off.
      if (this.fastGenerating || this.fastDone) {
        return { ctaLabel: "", disabled: true, busy: this.fastGenerating, hideNext: true };
      }
      if (!this.hasHeadings) {
        return { ctaLabel: this.subApproach === "leave" ? "Continue →" : "✦ Generate sub-headings",
                 disabled: false, busy: this.busy || this.generatingStructure,
                 blockingReason: this.footerError || "", hideNext: false };
      }
      if (!this.blocksGenerated) {
        return { ctaLabel: "⚡ Generate draft →", disabled: false,
                 busy: this.busy || this.drafting,
                 blockingReason: this.footerError || "", hideNext: false };
      }
      return { ctaLabel: "Continue to SEO & Quality →", disabled: false, busy: this.busy,
               blockingReason: this.footerError || "", hideNext: false };
    },
  },
  watch: {
    barState: { immediate: true, handler(v) { this.$emit("bar", v); } },
    // Fast Draft finished: load the generated blocks and open the Content tab so
    // the article is shown for review (the article wrapper renders once
    // fastGenerating is false). DraftStep is already mounted while generating, so
    // mounted() can't cover this.
    fastDone(done) {
      if (done) { this.tab = "content"; this.loadBlocks(); }
    },
  },
  created() {
    // Defensive: never land on the disabled "leave them as is" for unbuilt pages.
    if (this.pageNotBuilt && this.subApproach === "leave") this.subApproach = "generate";
  },
  async mounted() {
    // In Fast Draft there's no structure/blocks to load yet — POP generates
    // everything in one pass while the progress panel animates (and stays put
    // once done, until the user clicks through to review).
    if (this.fastGenerating || this.fastDone) return;
    // Do NOT auto-generate the outline — the user triggers sub-heading
    // generation explicitly (empty-state button / bottom-bar CTA).
    await this.loadBlocks();
  },
  beforeDestroy() {
    if (this._poll) clearTimeout(this._poll);
    if (this._saveTimer) clearTimeout(this._saveTimer);
    clearInterval(this._genTrickle);
  },
  methods: {
    // Structure planning is a single opaque LLM call (server only reports 20% →
    // 100%), so ease a determinate bar toward a 90% cap while it runs; completion
    // snaps it to 100%. Start/complete are real; the middle is a smooth estimate.
    startGenProgress() {
      this.genPct = 8;
      clearInterval(this._genTrickle);
      this._genTrickle = setInterval(() => {
        this.genPct = Math.min(90, this.genPct + Math.max(0.5, (90 - this.genPct) * 0.05));
      }, 240);
    },
    stopGenProgress() {
      clearInterval(this._genTrickle);
      this._genTrickle = null;
    },
    // Fast Draft stage status for the progress list (done / active / pending).
    fastStageStatus(i) {
      if (this.fastDone) return "done";
      if (this.fastComplete) return "done";
      const cur = this.fastStageIndex;
      if (i < cur) return "done";
      if (i === cur) return "active";
      return "pending";
    },
    // Invoked by the shared bottom bar's Next button (via Index $refs.activeStep).
    barNext() {
      if (!this.hasHeadings) this.onGenerateStructure();
      else if (!this.blocksGenerated) this.generateDraft();
      else this.continueToSeo();
    },
    // Config action: generate the outline, or keep the existing one as-is.
    onGenerateStructure() {
      if (this.subApproach === "leave") return this.leaveAsIs();
      // With an outline already present this button is a *regeneration*, so route
      // it through the credit confirmation like the Structure toolbar's
      // regenerate-all — the plain generate endpoint is free (charge: False) and
      // would otherwise let a user regenerate indefinitely at no cost.
      if (this.hasHeadings) return this.openRegenAll();
      return this.generateStructure();
    },
    pollPhase(phase) {
      return new Promise((resolve) => {
        const tick = async () => {
          try {
            const res = await aiWriterGetProgress(this.token, this.aiRunId);
            const p = res?.data?.progress || {};
            if (p.phase === phase) {
              this.progress = p;
              if (p.status === "complete" || p.status === "failed") return resolve(p);
            }
            // A task that died (worker restart / timeout) leaves progress pinned at
            // running; the server flags it stale so we stop waiting forever.
            if (p.stale) return resolve(p);
          } catch (e) { /* keep polling */ }
          this._poll = setTimeout(tick, 1200);
        };
        this._poll = setTimeout(tick, 700);
      });
    },
    async reloadStructure() {
      try {
        const res = await aiWriterGetState(this.token, this.aiRunId);
        const s = res?.data?.session?.structure || {};
        this.rows = (s.rows || []).slice();
        // The outline changed — have the parent re-fetch the session so the
        // sidebar's term boxes (Sub-headings especially) reflect the new
        // headings without needing a page reload.
        this.$emit("credits-changed");
        this.markContentChanged();
      } catch (e) { /* ignore */ }
    },
    // The progress bar renders near the top of the step; scroll up to it so the
    // user sees generation is running (the window doesn't scroll — the editor
    // pane does — so use scrollIntoView, not window.scrollTo).
    scrollTop() {
      this.$nextTick(() => {
        if (this.$el && this.$el.scrollIntoView) {
          this.$el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    async generateStructure() {
      this.generatingStructure = true;
      this.startGenProgress();
      this.scrollTop();
      this.footerError = "";
      try {
        const res = await aiWriterStructureGenerate(this.token, this.aiRunId);
        if (res?.data?.status === "SUCCESS") {
          const p = await this.pollPhase("structure");
          this.genPct = 100;
          await this.reloadStructure();
          // The task can still fail (or time out) after a successful enqueue, or
          // come back with rows but no headings — don't leave the user on the empty
          // config screen with no explanation.
          if (!this.hasHeadings) {
            this.footerError = (p && p.error && p.error.message)
              || "POP couldn't build an outline. Please try again.";
          }
        } else {
          this.footerError = res?.data?.error || "Could not plan the outline.";
        }
      } finally {
        this.stopGenProgress();
        this.generatingStructure = false;
      }
    },
    // ---- structure edits ----
    scheduleSave() {
      if (this._saveTimer) clearTimeout(this._saveTimer);
      this._saveTimer = setTimeout(() => this.saveStructure(), 500);
    },
    async saveStructure() {
      try {
        await aiWriterStructureSave(this.token, this.aiRunId, this.rows);
        // Edited/reordered headings change sub-heading term coverage — refresh
        // the sidebar. Text edits are already debounced via scheduleSave().
        this.$emit("credits-changed");
        this.markContentChanged();
      } catch (e) { /* ignore */ }
    },
    onReorder(newRows) { this.rows = newRows; this.saveStructure(); },
    onEditText({ row, text }) { row.text = text; this.scheduleSave(); },
    onToggleKind(row) {
      if (row.kind === "h2") row.kind = "h3";
      else if (row.kind === "h3") row.kind = "h2";
      this.saveStructure();
    },
    onLockRow(row) { row.locked = !row.locked; this.saveStructure(); },
    onCopyRow(row) { if (navigator.clipboard) navigator.clipboard.writeText(row.text || ""); },
    onDeleteRow(row) { this.rows = this.rows.filter((r) => r.id !== row.id); this.saveStructure(); },
    onAddHeading() {
      this.rows.push({ id: "row_" + Math.random().toString(36).slice(2, 10), kind: "h2", text: "New section", locked: false, order: (this.rows.length + 1) * 10, metadata: {} });
      this.saveStructure();
    },
    openBulkHeadings() {
      this.bulkModal = { show: true, text: "", error: "" };
    },
    confirmBulkHeadings() {
      const parsed = parseHeadingsHtml(this.bulkModal.text);
      if (!parsed.length) {
        this.bulkModal.error = "Only <h2> and <h3> tags are allowed. No paragraphs, spans, or plain text.";
        return;
      }
      // An H3 without parent_id materializes as an orphan block the draft never
      // writes, so nest each H3 under the sub-heading above it; an H3 with no H2
      // above becomes a top-level section (same rule as drag-and-drop).
      const newId = () => "row_" + Math.random().toString(36).slice(2, 10);
      let lastH2 = (this.rows.filter((r) => r.kind === "h2").slice(-1)[0] || {}).id || null;
      const added = parsed.map((h) => {
        const id = newId();
        if (h.kind === "h3" && lastH2) {
          return { id, kind: "h3", text: h.text, parent_id: lastH2, locked: false, order: 0, metadata: {} };
        }
        lastH2 = id;
        return { id, kind: "h2", text: h.text, parent_id: null, locked: false, order: 0, metadata: {} };
      });
      this.rows = this.rows.concat(added);
      this.rows.forEach((r, i) => { r.order = (i + 1) * 10; });
      this.bulkModal.show = false;
      this.saveStructure();
    },
    onAddVisual(type) {
      this.rows.push({ id: "row_" + Math.random().toString(36).slice(2, 10), kind: "visual", visual_type: type, locked: false, order: (this.rows.length + 1) * 10, metadata: {} });
      this.saveStructure();
    },
    openRegenAll() { this.footerError = ""; this.regenAllModal = { show: true, text: "", picked: [] }; },
    toggleRegenAllSugg(text) {
      const i = this.regenAllModal.picked.indexOf(text);
      if (i !== -1) { this.regenAllModal.picked.splice(i, 1); return; }
      this.regenAllModal.picked.push(text);
      this.regenAllModal.text = (this.regenAllModal.text ? this.regenAllModal.text.trim() + "\n" : "") + text;
    },
    async confirmRegenAll() {
      this.busy = true;
      // Show the same determinate planning bar as first-time generate. (busy stays
      // set for the toolbar's :disabled; the bar itself is gated on
      // generatingStructure so the other structure edits don't trigger it.)
      this.generatingStructure = true;
      this.startGenProgress();
      this.scrollTop();
      try {
        const res = await aiWriterStructureRegenerate(this.token, this.aiRunId, { confirm: true, instruction: this.regenAllModal.text });
        if (res?.data?.status === "SUCCESS") {
          this.regenAllModal.show = false;
          await this.pollPhase("structure");
          this.genPct = 100;
          // reloadStructure() emits credits-changed, refreshing the session.
          await this.reloadStructure();
        } else {
          this.footerError = res?.data?.error || "Could not regenerate.";
        }
      } finally {
        this.stopGenProgress();
        this.generatingStructure = false;
        this.busy = false;
      }
    },
    async downloadOutline() {
      try {
        const res = await aiWriterStructureExport(this.token, this.aiRunId);
        const html = res?.data?.outline?.html || "";
        downloadAsHtml("outline.html", html);
      } catch (e) { /* ignore */ }
    },
    copyOutline() {
      if (navigator.clipboard) navigator.clipboard.writeText(outlineHtmlFromRows(this.rows));
    },
    async leaveAsIs() {
      this.busy = true;
      try {
        let res = await aiWriterStructureLeaveAsIs(this.token, this.aiRunId);
        // The source page (e.g. the URL being optimized) may need scraping first;
        // the backend does it async — poll the 'scrape' phase, then retry.
        if (res?.data?.status === "PROCESSING") {
          await this.pollPhase("scrape");
          res = await aiWriterStructureLeaveAsIs(this.token, this.aiRunId);
        }
        await this.reloadStructure();
      } catch (e) { /* ignore */ } finally {
        this.busy = false;
      }
    },
    // ---- draft ----
    async generateDraft() {
      this.busy = true;
      this.footerError = "";
      this.drafting = true;
      this.tab = "content";
      this.scrollTop();
      try {
        const res = await aiWriterDraftGenerate(this.token, this.aiRunId);
        if (res?.data?.status === "SUCCESS") {
          await this.pollPhase("draft");
          await this.loadBlocks();
          // Draft scores/word-count/term-coverage + the credit charge are now
          // persisted — have the parent re-fetch the session so the sidebar
          // (word count, POP score, breakdown, term coverage) and balance update.
          this.$emit("credits-changed");
          // Fresh draft = freshly scored, so the "Update optimization score" button
          // starts disabled until the next edit.
          this.$emit("content-scored");
        } else {
          this.drafting = false;
          this.footerError = res?.data?.code === "insufficient_credits"
            ? "Not enough credits to generate the draft."
            : (res?.data?.error || "Could not generate the draft.");
        }
      } finally {
        this.busy = false;
        this.drafting = false;
      }
    },
    async loadBlocks() {
      try {
        const res = await aiWriterGetBlocks(this.token, this.aiRunId);
        const bj = res?.data?.blocks || {};
        this.blocks = bj.blocks || [];
        this.blocksGenerated = !!bj.generated_at;
      } catch (e) { /* ignore */ }
    },
    // ---- block ops ----
    // The optimization score is computed from the current content; any edit makes
    // it stale until the user clicks "Update optimization score" (see Index.vue).
    markContentChanged() {
      if (this.blocksGenerated) this.$emit("content-changed");
    },
    async onBlockEdit({ block, html }) {
      await aiWriterBlockPatch(this.token, this.aiRunId, block.id, { html });
      await this.loadBlocks();
      this.markContentChanged();
    },
    async onBlockLock(block) {
      await aiWriterBlockPatch(this.token, this.aiRunId, block.id, { locked: !block.locked });
      await this.loadBlocks();
    },
    // TOC style/H3/label options: server stores them on the block's metadata and
    // rebuilds the (system-built) html — free, no regenerate credit.
    async onTocOptions({ block, ...opts }) {
      await aiWriterBlockPatch(this.token, this.aiRunId, block.id, opts);
      await this.loadBlocks();
    },
    async onBlockDelete(block) {
      await aiWriterBlockDelete(this.token, this.aiRunId, block.id);
      await this.loadBlocks();
      this.markContentChanged();
    },
    async onAltEdit({ block, alt }) {
      await aiWriterBlockPatch(this.token, this.aiRunId, block.id, { alt });
      this.markContentChanged();
    },
    openRegenConfirm({ block, mode }) {
      this.regenError = "";
      this.regenModal = { show: true, block, mode };
    },
    async confirmRegen() {
      await this.doRegen(this.regenModal.block, this.regenModal.mode, "");
    },
    openAgainModal(block) {
      this.regenError = "";
      this.againModal = { show: true, text: "", block, picked: [] };
      this.$nextTick(() => { if (this.$refs.raInput) this.$refs.raInput.focus(); });
    },
    // Chips toggle their selected state; the text is appended on activation only
    // (deselecting leaves what the user already has, matching the design).
    toggleSugg(text) {
      const i = this.againModal.picked.indexOf(text);
      if (i !== -1) {
        this.againModal.picked.splice(i, 1);
        return;
      }
      this.againModal.picked.push(text);
      this.againModal.text = (this.againModal.text ? this.againModal.text.trim() + "\n" : "") + text;
    },
    async confirmAgain() {
      await this.doRegen(this.againModal.block, "again", this.againModal.text);
    },
    async doRegen(block, mode, instruction) {
      this.busy = true;
      this.regenError = "";
      try {
        const res = await aiWriterBlockRegenerate(this.token, this.aiRunId, block.id, { mode, instruction, confirm: true });
        if (res?.data?.status === "SUCCESS") {
          this.regenModal.show = false;
          this.againModal.show = false;
          await this.pollPhase("block_regen");
          await this.loadBlocks();
          this.$emit("credits-changed");
          this.markContentChanged();
        } else if (res?.data?.code === "insufficient_credits") {
          this.regenError = "Not enough credits.";
        } else {
          this.regenError = res?.data?.error || "Could not regenerate.";
        }
      } finally {
        this.busy = false;
      }
    },
    async openHistory(block) {
      this.historyModal = { show: true, items: [], block };
      try {
        const res = await aiWriterBlockHistory(this.token, this.aiRunId, block.id);
        this.historyModal.items = res?.data?.history || [];
      } catch (e) { /* ignore */ }
    },
    async restoreVersion(versionId) {
      await aiWriterBlockRestore(this.token, this.aiRunId, this.historyModal.block.id, versionId);
      this.historyModal.show = false;
      await this.loadBlocks();
      this.markContentChanged();
    },
    async openStructureHistory() {
      this.structHistoryModal = { show: true, items: [] };
      try {
        const res = await aiWriterStructureHistory(this.token, this.aiRunId);
        this.structHistoryModal.items = res?.data?.history || [];
      } catch (e) { /* ignore */ }
    },
    async restoreStructureVersion(versionId) {
      try {
        await aiWriterStructureRestore(this.token, this.aiRunId, versionId);
        this.structHistoryModal.show = false;
        await this.reloadStructure();
      } catch (e) { /* ignore */ }
    },
    async onGenerateImage(block) {
      // Serialize image generation: only one image task at a time. Running two
      // concurrently corrupts the shared blocks_json blob (wrong image / mis-count).
      if (this.imageBusy) return;
      this.imageBusy = true;
      // Optimistic "generating" state so the block shows the spinner immediately
      // (the server only flips it after the task starts).
      if (!block.metadata) this.$set(block, "metadata", {});
      this.$set(block.metadata, "asset", { ...(block.metadata.asset || {}), status: "generating" });
      const revert = () => {
        const a = block.metadata && block.metadata.asset;
        if (a && a.status === "generating") {
          this.$set(block.metadata, "asset", { ...a, status: a.filename ? "done" : "error" });
        }
      };
      const fail = (msg) => { revert(); this.$store.commit("setGlobalErrorMsg", { msg }); };
      try {
        const res = await aiWriterBlockImage(this.token, this.aiRunId, block.id, {});
        if (res?.data?.status === "SUCCESS") {
          const p = await this.pollPhase("image");
          await this.loadBlocks();
          this.$emit("credits-changed");
          if (p && p.status === "failed") {
            this.$store.commit("setGlobalErrorMsg", { msg: (p.error && p.error.message) || "Image generation failed." });
          }
        } else if (res?.data?.code === "insufficient_credits") {
          fail("Not enough credits to generate the image.");
        } else {
          fail(res?.data?.error || "Couldn't generate the image. Please try again.");
        }
      } catch (e) {
        fail("Couldn't generate the image. Please try again.");
      } finally {
        this.imageBusy = false;
      }
    },
    async continueToSeo() {
      this.busy = true;
      try {
        const res = await aiWriterContinueToSeo(this.token, this.aiRunId);
        if (res?.data?.status === "SUCCESS") this.$emit("continue");
      } finally {
        this.busy = false;
      }
    },
    _visualLabel(t) {
      const v = VISUAL_PLACEHOLDER_TYPES.find((x) => x.value === t);
      return v ? v.label : t;
    },
  },
};
</script>

<style scoped>
.aiw2-draft { margin: 0 auto; }
.aiw2-card { padding: 14px 18px; margin-bottom: 12px; }

/* ----- Fast Draft generation progress (design parity) ----- */
.draft-progress { background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); padding: 20px 22px; margin-top: 8px; }
.draft-progress-head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.draft-progress-head h4 { font-size: 16px; font-weight: 700; margin: 0; color: var(--text, #052e33); }
.draft-progress-head .word-pill { margin-left: auto; background: var(--surface-2, #f7f7f7); color: var(--text-3, #6b7b7e); font-size: 11px; padding: 2px 8px; border-radius: 4px; font-weight: 600; }
.progress-bar-wrap { height: 6px; background: var(--surface-2, #f7f7f7); border-radius: 999px; overflow: hidden; margin-bottom: 14px; border: 1px solid var(--border, #e4e4e4); }
.progress-bar { height: 100%; background: linear-gradient(90deg, var(--pop-orange, #ff5700) 0%, #ff9966 100%); width: 0%; transition: width 1.1s ease-out; border-radius: 999px; }
.progress-list { display: flex; flex-direction: column; gap: 2px; }
/* Icon aligned to the first line of the label (labels can wrap), not the vertical
   center of the whole wrapped block. */
.progress-row { display: flex; align-items: flex-start; gap: 12px; padding: 8px 0; font-size: 13.5px; }
.progress-row .check { width: 20px; height: 20px; border-radius: 50%; background: var(--pop-green, #25a97a); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; flex-shrink: 0; }
.progress-row.active .check { background: var(--pop-orange-soft, #ffeddd); color: var(--pop-orange, #ff5700); border: 1.5px solid var(--pop-orange, #ff5700); animation: aiw2-pulse 1.6s ease-in-out infinite; }
.progress-row.pending .check { background: var(--surface-2, #f7f7f7); color: var(--text-muted, #9aa6a8); border: 1.5px solid var(--border-strong, #ccc); }
@keyframes aiw2-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(255,87,0,.4); } 50% { box-shadow: 0 0 0 6px rgba(255,87,0,0); } }
.progress-row .label { flex: 1; font-weight: 500; color: var(--text-2, #014751); line-height: 20px; }
.progress-row.active .label { font-weight: 600; color: var(--text, #052e33); }
.progress-row.pending .label { color: var(--text-muted, #9aa6a8); }
.progress-row .spinner { width: 12px; height: 12px; border-radius: 50%; border: 1.5px solid transparent; border-top-color: var(--pop-orange, #ff5700); border-right-color: var(--pop-orange, #ff5700); animation: aiw2-spin 0.7s linear infinite; flex-shrink: 0; }
@keyframes aiw2-spin { to { transform: rotate(360deg); } }
.draft-progress-foot { margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--border, #e4e4e4); display: flex; gap: 10px; align-items: center; }
.current-preview { flex: 1; font-size: 12.5px; color: var(--text-3, #6b7b7e); }
.current-preview strong { color: var(--text, #052e33); font-weight: 600; }
/* ----- Sub-tabs (design parity) ----- */
/* No `overflow` here: it would clip the Structure toolbar's hover tooltips (which
   spill past the wrap edge). The tab header keeps its rounded top corners via its
   own border-radius, and this stays out of the way of the Content tab's sticky
   "Jump to" section-nav (which needs the real scroll parent). */
.draft-tabs-wrap { background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); margin-top: 4px; }
.draft-tabs { display: flex; background: var(--surface-2, #f7f7f7); border-bottom: 1px solid var(--border, #e4e4e4); padding: 4px; gap: 2px; border-top-left-radius: var(--r-lg, 10px); border-top-right-radius: var(--r-lg, 10px); }
.draft-tab { flex: 1; background: transparent; border: none; padding: 9px 14px; font-family: inherit; font-size: 13px; font-weight: 600; color: var(--text-3, #6b7b7e); cursor: pointer; border-radius: 6px; display: flex; align-items: center; justify-content: center; gap: 8px; transition: all .15s; }
.draft-tab:hover { color: var(--text, #052e33); }
.draft-tab:disabled { opacity: .5; cursor: not-allowed; }
.draft-tab.active { background: #fff; color: var(--text, #052e33); box-shadow: var(--shadow-sm, 0 1px 2px rgba(15,20,25,.03)); }
.draft-tab-num { width: 20px; height: 20px; border-radius: 50%; background: var(--surface-2, #f7f7f7); color: var(--text-3, #6b7b7e); font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
.draft-tab.active .draft-tab-num { background: var(--pop-orange, #ff5700); color: #fff; }
.draft-tab.done .draft-tab-num { background: var(--pop-green, #25a97a); color: #fff; }
.draft-tab.done .draft-tab-num::before { content: "✓"; font-weight: 700; }
.draft-tab.done .draft-tab-num > span { display: none; }
.draft-tab-panel { padding: 18px 20px; }
.loading-card { display: flex; align-items: center; gap: 10px; color: var(--text-3, #6b7b7e); justify-content: center; }
.st-intro { font-size: 13px; color: var(--text-3, #6b7b7e); margin: 0 2px 14px; line-height: 1.5; }
/* Bulma's global `strong` rule forces a near-black colour; keep the emphasis in
   the surrounding text colour (same workaround as .generate-cta p strong). */
.st-intro strong { color: inherit; font-weight: 700; }

/* ----- Generate-draft CTA ----- */
.generate-cta { background: linear-gradient(135deg, var(--pop-orange-soft, #ffeddd) 0%, #fff 100%); border: 1px solid var(--pop-orange-tint, #ffd9c2); border-radius: var(--r-lg, 10px); padding: 18px 22px; margin-top: 14px; display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.generate-cta-text { flex: 1; min-width: 200px; }
.generate-cta h4 { margin: 0 0 3px; font-size: 15px; font-weight: 700; color: var(--text, #052e33); }
.generate-cta p { margin: 0; font-size: 12.5px; color: var(--text-3, #6b7b7e); }
/* Keep the estimate numbers in the muted text color; Bulma's global `strong`
   rule would otherwise darken them to #363636. */
.generate-cta p strong { color: inherit; font-weight: 700; }

/* ----- Content tab empty ----- */
.tab-empty { text-align: center; padding: 36px 20px; color: var(--text-3, #6b7b7e); }
.tab-empty h4 { font-size: 15px; font-weight: 700; color: var(--text, #052e33); margin: 0 0 6px; }
.tab-empty p { font-size: 13px; margin: 0 0 14px; }

/* ----- Sub-headings config form (Stage A) ----- */
/* margin-bottom matches the design — separates the config card from the outline
   review that now renders below it. */
.title-config { background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); padding: 16px 20px; margin-bottom: 14px; }
.title-config-head { font-size: 13.5px; font-weight: 700; color: var(--text, #052e33); margin-bottom: 12px; display: flex; align-items: center; gap: 6px; }
.title-config-toggles { margin-top: 10px; padding-top: 12px; border-top: 1px solid var(--border, #e4e4e4); }
.tct-label { font-size: 10.5px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: var(--text-3, #6b7b7e); margin-bottom: 8px; }
.title-gen-bar { display: flex; align-items: center; gap: 14px; margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--border, #e4e4e4); }
/* Matches TitleStep's CTA icon — an SVG sparkle rather than a text glyph. */
.gen-ico { width: 14px; height: 14px; flex-shrink: 0; display: block; }
.title-progress { margin-top: 16px; }
.title-progress-label { font-size: 12.5px; font-weight: 600; color: var(--text-2, #014751); display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.title-progress .prog-glyph { display: inline-block; animation: draftSpin 1s linear infinite; }
.title-progress-track { height: 8px; border-radius: 999px; background: #e6e9f0; overflow: hidden; }
.title-progress-fill { height: 100%; width: 0; border-radius: 999px; background: var(--pop-orange, #ff5700); transition: width .3s ease; }
@keyframes draftSpin { to { transform: rotate(360deg); } }
.info-ico { width: 16px; height: 16px; display: inline-block; flex-shrink: 0; cursor: pointer; background-color: #014751; -webkit-mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>') center / contain no-repeat; mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>') center / contain no-repeat; }
.radio-row { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 7px; cursor: pointer; transition: background .15s; font-size: 13.5px; color: var(--text, #052e33); margin-bottom: 4px; }
.radio-row:hover { background: var(--surface-2, #f7f7f7); }
.radio-row.selected { background: var(--pop-orange-soft, #ffeddd); }
.radio-row.disabled { opacity: .55; cursor: not-allowed; }
.radio-row.disabled:hover { background: none; }
.radio-dot { width: 18px; height: 18px; border-radius: 50%; border: 1.5px solid var(--border-strong, #ccc); background: #fff; flex-shrink: 0; display: flex; align-items: center; justify-content: center; transition: all .15s; }
.radio-row.selected .radio-dot { border-color: var(--pop-orange, #ff5700); }
.radio-row.selected .radio-dot::after { content: ""; width: 9px; height: 9px; border-radius: 50%; background: var(--pop-orange, #ff5700); }
.toggle-row { display: flex; align-items: center; gap: 10px; font-size: 13.5px; color: var(--text-2, #014751); cursor: pointer; padding: 6px 0; }
.toggle { width: 36px; height: 20px; border-radius: 999px; background: var(--border-strong, #ccc); position: relative; cursor: pointer; transition: background .2s; flex-shrink: 0; border: none; padding: 0; }
.toggle::after { content: ""; position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #fff; transition: transform .2s; box-shadow: 0 1px 3px rgba(0,0,0,.2); }
.toggle.on { background: var(--pop-green, #25a97a); }
.toggle.on::after { transform: translateX(16px); }
.title-config-head .info-ico, .radio-row .info-ico { cursor: pointer; }
.err { color: var(--pop-red, #ff3939); font-size: 0.8rem; }
.aiw2-overlay { position: fixed; inset: 0; background: rgba(5,20,25,.35); display: flex; align-items: center; justify-content: center; z-index: 60; }
.aiw2-modal { background: #fff; border-radius: var(--r-lg, 10px); padding: 22px; width: 440px; max-width: 92vw; box-shadow: var(--shadow-lg, 0 10px 36px rgba(15,20,25,.08)); font-family: "Mulish", system-ui, sans-serif; }
.aiw2-modal h4 { font-size: 1rem; font-weight: 800; margin-bottom: 8px; }
.aiw2-modal p { font-size: 0.85rem; color: var(--text-2, #014751); margin-bottom: 12px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 14px; }
.suggs { display: flex; gap: 6px; flex-wrap: wrap; margin: 8px 0; }

/* ----- "Regenerate this section" modal — 1:1 with the design handoff's
   .regen-overlay / .regen-modal.regen-again. Kept separate from the shared
   .aiw2-overlay/.aiw2-modal so the other modals are unaffected. ----- */
.regen-overlay { position: fixed; inset: 0; z-index: 200; background: rgba(5, 46, 51, .55); display: flex; align-items: center; justify-content: center; padding: 24px; }
.regen-modal { background: #fff; border-radius: 16px; box-shadow: 0 24px 70px rgba(0,0,0,.35); width: 100%; max-width: 620px; padding: 28px 30px 24px; animation: regenPop .2s ease both; font-family: "Mulish", system-ui, sans-serif; }
@keyframes regenPop { from { opacity: 0; transform: translateY(10px) scale(.98); } to { opacity: 1; transform: none; } }
.regen-modal.regen-again { max-width: 480px; padding: 22px 24px 20px; text-align: left; }
.regen-again .ra-head { display: flex; align-items: center; margin-bottom: 14px; }
.regen-again .ra-head h3 { font-family: inherit; margin: 0; font-size: 18px; font-weight: 800; color: var(--text, #052e33); }
.regen-again .ra-close { margin-left: auto; width: 30px; height: 30px; border: none; background: transparent; font-size: 22px; line-height: 1; color: var(--text-3, #6b7b7e); cursor: pointer; border-radius: 7px; }
.regen-again .ra-close:hover { background: var(--surface-2, #f7f7f7); color: var(--text, #052e33); }
.regen-again .ra-label { display: block; font-size: 12.5px; font-weight: 700; color: var(--text-2, #014751); margin-bottom: 7px; }
.regen-again .ra-label-note { font-weight: 500; color: var(--text-3, #6b7b7e); }
.regen-again .ra-input { width: 100%; box-sizing: border-box; resize: vertical; font-family: inherit; font-size: 13.5px; color: var(--text, #052e33); line-height: 1.5; border: 1px solid var(--border, #e4e4e4); border-radius: 9px; padding: 11px 13px; background: #fff; }
.regen-again .ra-input:focus { outline: none; border-color: var(--pop-orange, #ff5700); box-shadow: 0 0 0 3px var(--pop-orange-soft, #ffeddd); }
.regen-again .ra-sugg-label { font-size: 11px; font-weight: 700; letter-spacing: .03em; text-transform: uppercase; color: var(--text-3, #6b7b7e); margin: 16px 0 8px; }
.regen-again .ra-suggs { display: flex; flex-wrap: wrap; gap: 8px; }
.regen-again .ra-sugg { font-family: inherit; font-size: 12.5px; font-weight: 600; color: var(--text-2, #014751); background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: 999px; padding: 7px 14px; cursor: pointer; transition: all .12s; }
.regen-again .ra-sugg:hover { background: var(--surface-2, #f7f7f7); border-color: var(--border-strong, #ccc); }
.regen-again .ra-sugg.active { background: var(--pop-orange, #ff5700); color: #fff; border-color: var(--pop-orange, #ff5700); }
.regen-again .ra-foot { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--border, #e4e4e4); }
.regen-again .ra-cost { font-size: 12.5px; color: var(--text-2, #014751); display: inline-flex; align-items: center; gap: 7px; }
/* Same Bulma `strong` override as above — the design has no .ra-cost strong rule,
   so the bolded credit count inherits the footer's colour. */
.regen-again .ra-cost strong { color: inherit; font-weight: 700; }
.regen-again .ra-cost-ico { color: var(--pop-orange, #ff5700); line-height: 0; }
.regen-again .ra-cost-ico svg { width: 16px; height: 16px; }
.regen-again .ra-actions { margin-left: auto; display: inline-flex; gap: 8px; }
.regen-again .btn-cancel, .regen-again .btn-regen { font-family: inherit; font-size: 14px; font-weight: 700; padding: 11px 26px; border-radius: 999px; cursor: pointer; }
.regen-again .btn-cancel { background: #fff; border: 1.5px solid var(--text, #052e33); color: var(--text, #052e33); }
.regen-again .btn-cancel:hover { background: var(--surface-2, #f7f7f7); }
.regen-again .btn-regen { background: var(--pop-orange, #ff5700); border: 1.5px solid var(--pop-orange, #ff5700); color: #fff; }
.regen-again .btn-regen:hover { background: var(--pop-orange-deep, #b23d00); border-color: var(--pop-orange-deep, #b23d00); }
.regen-again .btn-regen:disabled { background: var(--border, #e4e4e4); border-color: var(--border, #e4e4e4); color: var(--text-3, #6b7b7e); cursor: default; }
.spin { width: 16px; height: 16px; border: 2px solid var(--border, #e4e4e4); border-top-color: var(--pop-orange, #ff5700); border-radius: 50%; display: inline-block; animation: aiw2-spin .7s linear infinite; }
.spin.light { width: 12px; height: 12px; border-color: rgba(255,255,255,.5); border-top-color: #fff; }
@keyframes aiw2-spin { to { transform: rotate(360deg); } }
</style>
